
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, MapPin, CreditCard, Package } from 'lucide-react';
import { Order } from '../../types';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id || !user) return;
      
      try {
        const docRef = doc(db, 'orders', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
           // Security check: ensure user owns this order
          if (data.userId !== user.uid) {
             navigate('/profile/orders'); // Redirect if not authorized
             return;
          }
          setOrder({ id: docSnap.id, ...data } as Order);
        } else {
          console.error("Order not found");
        }
      } catch (err) {
        console.error("Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, user, navigate]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center">Loading...</div>;
  if (!order) return <div className="p-8 text-center bg-gray-50 min-h-[50vh]">Order not found</div>;

  const date = order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString() : 'N/A';
  const address = order.shippingDetails?.address;

  return (
    <div className="max-w-3xl mx-auto py-12 px-4">
      <button onClick={() => navigate('/profile/orders')} className="flex items-center text-sm text-gray-500 hover:text-black mb-8 transition-colors">
        <ArrowLeft size={16} className="mr-2" /> Back to Orders
      </button>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-gray-200 pb-6 mb-8 gap-4">
        <div>
           <div className="flex items-center gap-3 mb-2">
             <h1 className="font-heading text-2xl tracking-widest">ORDER #{order.id}</h1>
             <span className={`font-mono text-xs px-2 py-0.5 rounded-full ${
                order.status === 'paid' ? 'text-green-600 bg-green-50' : 'text-yellow-600 bg-yellow-50'
             }`}>
               {order.status.toUpperCase()}
             </span>
           </div>
           <p className="text-gray-500 text-sm">Placed on {date}</p>
        </div>
        <div className="text-right">
             <p className="font-heading text-xl font-bold">£{order.totalAmount?.toFixed(2)}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-12">
        <div className="col-span-2 space-y-6">
            <h3 className="font-heading text-lg border-b border-gray-100 pb-2">ITEMS</h3>
            {order.items?.map((item, idx) => (
                <div key={idx} className="flex gap-4">
                    <div className="w-20 h-20 bg-gray-100 flex-shrink-0">
                        <img src={item.image || '/assets/placeholder.svg'} alt={item.title} className="w-full h-full object-cover" />
                    </div>
                    <div>
                        <p className="font-bold text-sm tracking-wide">{item.title}</p>
                        {item.editionNumbers && item.editionNumbers.length > 0 && (
                            <p className="text-xs text-gray-700 font-mono mt-0.5">
                                {item.editionNumbers.map(n => `No. ${n}`).join(', ')}
                            </p>
                        )}
                        {item.selectedSize && <p className="text-xs text-gray-500">Size: {item.selectedSize}</p>}
                        {item.selectedColor && <p className="text-xs text-gray-500">Color: {item.selectedColor}</p>}
                        <p className="text-xs text-gray-500 mt-1">Qty: {item.quantity}</p>
                    </div>
                    <div className="ml-auto">
                        <p className="font-mono text-sm">£{item.price.toFixed(2)}</p>
                    </div>
                </div>
            ))}
        </div>

        <div className="space-y-8">
            <div>
                <h3 className="font-heading text-sm text-gray-400 mb-4 flex items-center gap-2"><MapPin size={14}/> SHIPPING DETAILS</h3>
                {address ? (
                    <div className="text-sm text-gray-600 space-y-1">
                        <p className="font-bold text-gray-900">{order.shippingDetails?.name}</p>
                        <p>{address.line1}</p>
                        {address.line2 && <p>{address.line2}</p>}
                        <p>{address.city}, {address.state} {address.postal_code}</p>
                        <p>{address.country}</p>
                    </div>
                ) : (
                    <p className="text-sm text-gray-400">No shipping info available</p>
                )}
            </div>

            <div>
                 <h3 className="font-heading text-sm text-gray-400 mb-4 flex items-center gap-2"><CreditCard size={14}/> CUSTOMER</h3>
                 <div className="text-sm text-gray-600 space-y-1">
                     <p>{order.customerDetails?.name}</p>
                     <p>{order.customerDetails?.email}</p>
                 </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
